import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
  Box,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import config from '../config';

const server_URL = config.SERVER_URL;

const TriggerAutomations = () => {
  const [open, setOpen] = useState(false);
  const [newAutomation, setNewAutomation] = useState({
    method: 'add',
    name: '',
    customer_id: '',
    template_id: '',
    server_id: '',
    custom_field_tag: '',
    subject: '',
  });
  const [automations, setAutomations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [servers, setServers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [events, setEvents] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loggedUserId, setLoggedUserId] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('');
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });
  const [togglingId, setTogglingId] = useState(null);

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  const handleOpen = async () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const customerId = userRole === 2 ? loggedUserId : '';
    
    setNewAutomation({
      method: 'add',
      name: '',
      customer_id: customerId,
      template_id: '',
      server_id: '',
      trigger_type: '',
      custom_field_tag: '',
      event_id: '',
      subject: '',
    });
    
    // If customer is auto-selected (role 2), fetch dependent data
    if (customerId) {
      await Promise.all([
        fetchTemplates(customerId),
        fetchServers(customerId),
        fetchCustomFields(customerId)
      ]);
    } else {
      setTemplates([]);
      setServers([]);
      setCustomFields([]);
    }
    
    setOpen(true);
  };

  const handleClose = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setNewAutomation({
      method: 'add',
      name: '',
      customer_id: userRole === 2 ? loggedUserId : '',
      template_id: '',
      server_id: '',
      trigger_type: '',
      custom_field_tag: '',
      event_id: '',
      subject: '',
    });
    setTemplates([]);
    setServers([]);
    setCustomFields([]);
    setOpen(false);
  };

  useEffect(() => {
    fetchAutomations();
    // Get logged user info
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    setLoggedUserId(userData.id || null);
    // If role is 2, set selected customer to logged user
    if (userData.role_id === 2 && userData.id) {
      setNewAutomation(prev => ({ ...prev, customer_id: userData.id }));
    }
    // Events will be fetched when customer is selected and trigger type is Event
  }, []);

  useEffect(() => {
    if (userRole !== null && loggedUserId !== null) {
      fetchCustomers();
    }
  }, [userRole, loggedUserId]);

  // Fetch dependent data when modal opens and customer is already selected
  useEffect(() => {
    if (open && newAutomation.customer_id && newAutomation.method === 'add') {
      fetchTemplates(newAutomation.customer_id);
      fetchServers(newAutomation.customer_id);
      fetchCustomFields(newAutomation.customer_id);
    }
  }, [open, newAutomation.customer_id, newAutomation.method]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(server_URL + '/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: loggedUserId,
          role_id: userRole
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
        // For filter dropdown: role 1 sees all, role 2 sees only logged customer
        if (userRole === 1) {
          setFilterCustomers(data.data);
        } else if (userRole === 2 && loggedUserId) {
          setFilterCustomers(data.data.filter(customer => customer.id === loggedUserId));
        }
        
        // Auto-select if only one customer and fetch dependent data
        if (data.data.length === 1) {
          const customerId = data.data[0].id;
          setNewAutomation(prev => ({ ...prev, customer_id: customerId }));
          // Fetch dependent data for auto-selected customer
          if (customerId) {
            fetchTemplates(customerId);
            fetchServers(customerId);
            fetchCustomFields(customerId);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchAutomations = async (filterCustomerId = null) => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'list',
        user_id: userData.id || null,
        role_id: userData.role_id || null
      };
      
      // Priority: explicit customer filter > role-based filtering
      if (filterCustomerId !== null && filterCustomerId !== '') {
        const customerIdValue = String(filterCustomerId) === '0' ? 0 : parseInt(filterCustomerId, 10);
        requestBody.customer_id = customerIdValue;
        console.log('Filtering by customer_id:', customerIdValue);
      } else if (userData.role_id === 2 && userData.id) {
        // For role 2 users, automatically filter by their own customer_id
        requestBody.customer_id = userData.id;
        console.log('Role 2 user - filtering by own customer_id:', userData.id);
      }
      
      console.log('Request body:', requestBody);
      
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch trigger automations');
      }

      const data = await response.json();
      console.log('Received trigger automations data:', data);
      
      const automationsData = Array.isArray(data) ? data : data.data || [];
      setAutomations(automationsData);
      
      if (automationsData.length === 0) {
        console.log('No trigger automations found matching the filter criteria');
      } else {
        console.log(`Found ${automationsData.length} trigger automation(s)`);
      }
    } catch (error) {
      console.error('Error fetching trigger automations:', error);
      setAutomations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = () => {
    fetchAutomations(customerFilter);
  };

  const handleClearFilter = () => {
    setCustomerFilter('');
    fetchAutomations();
  };

  const fetchTemplates = async (customerId) => {
    if (!customerId) {
      setTemplates([]);
      return;
    }
    try {
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_templates',
          customer_id: customerId,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setTemplates(data.data);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const fetchServers = async (customerId) => {
    if (!customerId) {
      setServers([]);
      return;
    }
    try {
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_servers',
          customer_id: customerId,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setServers(data.data);
      } else {
        setServers([]);
      }
    } catch (error) {
      console.error('Error fetching servers:', error);
      setServers([]);
    }
  };


  const fetchCustomFields = async (customerId) => {
    if (!customerId) {
      setCustomFields([]);
      return;
    }
    try {
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_custom_fields',
          customer_id: customerId,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomFields(data.data);
      } else {
        setCustomFields([]);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      setCustomFields([]);
    }
  };

  const fetchEvents = async (customerId = null) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'get_events',
        user_role: userData.role_id,
        user_id: userData.id,
      };
      
      // Add customer_id if provided (for filtering events)
      if (customerId) {
        requestBody.customer_id = customerId;
      }
      
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setEvents(data.data);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const handleCustomerChange = (customerId) => {
    setNewAutomation(prev => ({
      ...prev,
      customer_id: customerId,
      template_id: '',
      server_id: '',
      custom_field_tag: '',
      event_id: '', // Clear event_id when customer changes
    }));
    if (customerId) {
      fetchTemplates(customerId);
      fetchServers(customerId);
      fetchCustomFields(customerId);
      
      // Fetch events for the selected customer (if trigger type is Event)
      if (newAutomation.trigger_type === 'Event') {
        fetchEvents(customerId);
      }
    } else {
      setTemplates([]);
      setServers([]);
      setCustomFields([]);
      setEvents([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newAutomation.name || !newAutomation.customer_id || !newAutomation.template_id || 
        !newAutomation.server_id || !newAutomation.subject) {
      showToast('Please fill in all required fields', 'error');
      return;
    }
    
    setIsSubmitting(true);
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const requestBody = {
      method: newAutomation.method,
      name: newAutomation.name,
      customer_id: newAutomation.customer_id,
      template_id: newAutomation.template_id,
      server_id: newAutomation.server_id,
      trigger_type: newAutomation.trigger_type || null,
      custom_field_tag: newAutomation.custom_field_tag || null,
      event_id: newAutomation.event_id || null,
      subject: newAutomation.subject,
      user_id: userData.id || null,
      is_subuser: userData.is_subuser || false,
      subuser_id: userData.subuser_id || null,
    };

    if (newAutomation.method === 'update') {
      requestBody.id = newAutomation.id;
    }

    try {
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      
      if (data.status === '1') {
        showToast(data.message || 'Trigger automation saved successfully', 'success');
        handleClose();
        await fetchAutomations(customerFilter);
      } else {
        showToast(data.error || 'Failed to save trigger automation', 'error');
      }
    } catch (error) {
      console.error('Error saving trigger automation:', error);
      showToast('An error occurred while saving trigger automation', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (automation) => {
    setNewAutomation({
      method: 'update',
      id: automation.id,
      name: automation.name,
      customer_id: automation.customer_id,
      template_id: automation.template_id,
      server_id: automation.server_id,
      trigger_type: automation.trigger_type || '',
      custom_field_tag: automation.custom_field_tag || '',
      event_id: automation.event_id || '',
      subject: automation.subject,
    });
    
    // Fetch dependent data
    if (automation.customer_id) {
      fetchTemplates(automation.customer_id);
      fetchServers(automation.customer_id);
      fetchCustomFields(automation.customer_id);
      
      // Fetch events if trigger type is Event and user is role 1
      if (automation.trigger_type === 'Event' && userRole === 1) {
        fetchEvents(automation.customer_id);
      }
    }
    
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this trigger automation?')) {
      return;
    }

    setDeletingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: id,
          user_id: userData.id || null,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        showToast('Trigger automation deleted successfully', 'success');
        await fetchAutomations(customerFilter);
      } else {
        showToast(data.error || 'Failed to delete trigger automation', 'error');
      }
    } catch (error) {
      console.error('Error deleting trigger automation:', error);
      showToast('Failed to delete trigger automation', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleEnable = async (id, currentEnabled) => {
    setTogglingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/trigger_automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'toggle_enable',
          id: id,
          enabled: currentEnabled ? 0 : 1,
          user_id: userData.id || null,
          is_subuser: userData.is_subuser || false,
          subuser_id: userData.subuser_id || null,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        showToast(data.message || `Trigger automation ${currentEnabled ? 'disabled' : 'enabled'} successfully`, 'success');
        await fetchAutomations(customerFilter);
      } else {
        showToast(data.error || 'Failed to update trigger automation', 'error');
      }
    } catch (error) {
      console.error('Error toggling trigger automation:', error);
      showToast('Failed to update trigger automation', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h1>Trigger Automations</h1>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Add Automation
        </Button>
      </div>

      {/* Customer Filter - Only show for role 1 (admin) */}
      {userRole === 1 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          bgcolor: '#f5f5f5',
          borderRadius: 1
        }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
              size="small"
            >
              <MenuItem value="">All Customers</MenuItem>
              {filterCustomers
                .filter(customer => customer.id !== 0)
                .map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.username} ({customer.email})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained"
            onClick={handleFilter}
            sx={{ height: '40px' }}
          >
            Filter
          </Button>
          {customerFilter !== '' && (
            <Button 
              variant="outlined"
              color="warning"
              onClick={handleClearFilter}
              sx={{ height: '40px' }}
            >
              Clear Filter
            </Button>
          )}
          <Button 
            variant="outlined"
            onClick={() => fetchAutomations(customerFilter)}
            sx={{ height: '40px' }}
          >
            Refresh
          </Button>
        </Box>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading trigger automations...</p>
          </div>
        </div>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {userRole === 1 && <TableCell>Customer</TableCell>}
                <TableCell>Name</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Server</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {automations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={userRole === 1 ? 8 : 7} align="center" sx={{ py: 5 }}>
                    <h5 style={{ color: '#666' }}>No trigger automations found</h5>
                    {customerFilter !== '' && (
                      <p style={{ color: '#999', fontSize: '0.875rem', marginTop: '8px' }}>
                        Try clearing the filter or selecting a different customer
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                automations.map((automation) => (
                  <TableRow key={automation.id}>
                    {userRole === 1 && (
                      <TableCell>
                        {automation.customer_username || 'N/A'}
                        {automation.customer_email && (
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {automation.customer_email}
                          </div>
                        )}
                      </TableCell>
                    )}
                    <TableCell>{automation.name}</TableCell>
                    <TableCell>{automation.template_name || 'N/A'}</TableCell>
                    <TableCell>{automation.server_name || 'N/A'}</TableCell>
                    <TableCell>
                      {automation.trigger_type === 'Event' && automation.event_name 
                        ? `${automation.trigger_type}: ${automation.event_name}` 
                        : automation.trigger_type || '-'}
                    </TableCell>
                    <TableCell>{automation.subject}</TableCell>
                    <TableCell>
                      <Chip 
                        label={automation.enabled ? 'Enabled' : 'Disabled'} 
                        color={automation.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color={automation.enabled ? 'warning' : 'success'}
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleToggleEnable(automation.id, automation.enabled)}
                        disabled={togglingId === automation.id}
                      >
                        {automation.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleEdit(automation)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(automation.id)}
                        disabled={deletingId === automation.id}
                      >
                        {deletingId === automation.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <h2>{newAutomation.method === 'add' ? 'Add Trigger Automation' : 'Edit Trigger Automation'}</h2>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Name"
                value={newAutomation.name}
                onChange={(e) => setNewAutomation(prev => ({ ...prev, name: e.target.value }))}
                required
              />

              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={newAutomation.customer_id}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  label="Customer"
                  disabled={userRole === 2}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.username} ({customer.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Template</InputLabel>
                <Select
                  value={newAutomation.template_id}
                  onChange={(e) => setNewAutomation(prev => ({ ...prev, template_id: e.target.value }))}
                  label="Template"
                  disabled={!newAutomation.customer_id}
                >
                  {templates.length === 0 ? (
                    <MenuItem disabled>No templates available. Select a customer first.</MenuItem>
                  ) : (
                    templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Email Server</InputLabel>
                <Select
                  value={newAutomation.server_id}
                  onChange={(e) => setNewAutomation(prev => ({ ...prev, server_id: e.target.value }))}
                  label="Email Server"
                  disabled={!newAutomation.customer_id}
                >
                  {servers.length === 0 ? (
                    <MenuItem disabled>No servers available. Select a customer first.</MenuItem>
                  ) : (
                    servers.map((server) => (
                      <MenuItem key={server.id} value={server.id}>
                        {server.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                  value={newAutomation.trigger_type}
                  onChange={(e) => {
                    const triggerType = e.target.value;
                    setNewAutomation(prev => {
                      const updated = { 
                        ...prev, 
                        trigger_type: triggerType,
                        event_id: triggerType === 'Event' ? prev.event_id : '' // Clear event_id if not Event type
                      };
                      
                    // Fetch events when Custom Event is selected and customer is selected
                    if (triggerType === 'Event' && updated.customer_id) {
                      fetchEvents(updated.customer_id);
                    }
                      
                      return updated;
                    });
                  }}
                  label="Trigger Type"
                >
                  <MenuItem value="">Select Trigger Type</MenuItem>
                  <MenuItem value="Birthday">Birthday</MenuItem>
                  <MenuItem value="Anniversary">Anniversary</MenuItem>
                  <MenuItem value="Event">Custom Event</MenuItem>
                </Select>
              </FormControl>

              {/* Show Event selection only when trigger type is Custom Event */}
              {newAutomation.trigger_type === 'Event' && (
                <FormControl fullWidth>
                  <InputLabel>Event</InputLabel>
                  <Select
                    value={newAutomation.event_id}
                    onChange={(e) => setNewAutomation(prev => ({ ...prev, event_id: e.target.value }))}
                    label="Event"
                  >
                    <MenuItem value="">Select Event</MenuItem>
                    {events.length === 0 ? (
                      <MenuItem disabled>No events available</MenuItem>
                    ) : (
                      events.map((event) => (
                        <MenuItem key={event.id} value={event.id}>
                          {event.name} ({event.key})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="Subject"
                value={newAutomation.subject}
                onChange={(e) => setNewAutomation(prev => ({ ...prev, subject: e.target.value }))}
                required
              />

              <FormControl fullWidth>
                <InputLabel>Custom Field</InputLabel>
                <Select
                  value={newAutomation.custom_field_tag}
                  onChange={(e) => setNewAutomation(prev => ({ ...prev, custom_field_tag: e.target.value }))}
                  label="Custom Field"
                  disabled={!newAutomation.customer_id}
                >
                  <MenuItem value="">None</MenuItem>
                  {customFields.map((field, index) => (
                    <MenuItem key={index} value={field.tag}>
                      {field.name} ({field.key})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : (newAutomation.method === 'add' ? 'Add' : 'Update')}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Modal>

      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastState(prev => ({ ...prev, open: false }))}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TriggerAutomations;

